import React, {useState, useEffect, useRef, useMemo} from 'react';
import Select, {components} from 'react-select';
import type {StylesConfig, CSSObjectWithLabel, SingleValue} from 'react-select';
import {FormControlLabel, Checkbox} from '@material-ui/core';
import {Label} from '../AdditionalSkills/AdditionalSkills';
import {Container, CommonLanguagesGrid} from './Languages.styles';
import { Language } from 'api/Api';

interface LanguagesProps {
    commonLanguages: Language[];
    otherLanguages: Language[];
    initialSelected?: Language[];
    onChange?: (langIds: number[]) => void;
    onClearError?: () => void;
    error?: string;
}

/**
 * Languages component renders a list of selectable languages. It manages its own
 * internal state to avoid triggering parent re-renders. Whenever the selection
 * changes it invokes `onChange` so the parent can store the data (e.g. in a ref).
 */
export default function Languages({commonLanguages, otherLanguages, initialSelected = [], onChange, error, onClearError}: LanguagesProps) {
    /*
     * We want to ensure that when the user decides to add an "Other" language on
     * mobile, the page doesn't auto-scroll in a way that hides the list of
     * languages. By keeping a ref to the root container we can explicitly
     * scroll the whole section into view (instead of just the input) once the
     * "Add Language" text field appears.
     */
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [selectedIds, setSelectedIds] = useState<number[]>(initialSelected.map(l=>l.id));
    const [customOptions, setCustomOptions] = useState<Language[]>(
        initialSelected.filter(l=>!commonLanguages.some(c=>c.id===l.id))
    );
    // Uncontrolled react-select input (no inputValue/onInputChange)

    const getVariants = (lang: Language) => lang.name.split(/[;,]/).map(v=>v.trim());
    const getNativeVariants = (lang: Language) => lang.native_name ? lang.native_name.split(/[;,]/).map(v=>v.trim()) : [];
    const getDisplayName = (lang: Language) => getVariants(lang)[0];

  useEffect(() => {
    onChange?.(selectedIds);
  }, [selectedIds, onChange]);

    /*
     * When we show the custom-language input (addingNew === true) mobile
     * browsers often try to auto-scroll the input to the very top of the
     * viewport – which means the list of checkboxes scrolls out of sight.
     * Manually scrolling the entire section into view ensures the user still
     * sees the list of available languages while the keyboard is open.
     */
    // When opening the select menu on mobile, ensure the section stays in view.
    const scrollSectionIntoView = () => {
        if (containerRef.current) {
            containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    type LangOption = { value: number; label: string; lang: Language };

    const otherOptions: LangOption[] = otherLanguages
        .filter(l => !commonLanguages.some(c => c.id === l.id))
        .map((l) => ({ value: l.id, label: getDisplayName(l), lang: l }));

    const toggleLanguage = (lang: Language, checked: boolean) => {
        setSelectedIds((prev) => {
            if (error) onClearError?.();
            if (checked) {
                return prev.includes(lang.id) ? prev : [...prev, lang.id];
            }
            return prev.filter((id) => id !== lang.id);
        });
    };

    /*
     * The parent component may load the user's languages asynchronously which
     * means this component can mount with an empty `initialSelected` array and
     * then receive the actual values on a later render.  We only want to merge
     * those initial values into our local state _once_ – afterwards the user
     * should be free to select / de-select languages without our state being
     * overwritten by subsequent re-renders of the parent (which would pass a
     * new array reference each time and previously caused the "can't de-select"
     * bug).
     */
    const hasInitialised = useRef(false);
    useEffect(() => {
        if (hasInitialised.current) return;
        if (initialSelected.length === 0) return; // nothing to initialise yet
        setSelectedIds(initialSelected.map((l) => l.id));
        const customLanguages = initialSelected.filter(
            (l) => !commonLanguages.some((c) => c.id === l.id)
        );
        if (customLanguages.length) {
            setCustomOptions((prev) => [...prev, ...customLanguages]);
        }
        hasInitialised.current = true;
    }, [initialSelected, commonLanguages]);

    /*
     * Helper that picks the first language whose name or variant either exactly
     * matches the input _or_ starts with it. This lets users type just a few
     * characters (e.g. "ger") and hit enter to select "German".
     */
    const filterOption = (candidate: any, input: string) => {
        const needle = input.trim().toLowerCase();
        if (!needle) return true;
        const l = candidate.data.lang;
        const variants = [...getVariants(l), ...getNativeVariants(l)];
        return variants.some((v) => {
            const hay = v.toLowerCase();
            return hay === needle || hay.startsWith(needle) || hay.includes(needle);
        });
    };

    const selectStyles: StylesConfig<LangOption, true> = useMemo(() => ({
        control: (base: CSSObjectWithLabel, state: { isFocused: boolean }) => ({
            ...base,
            minHeight: 52,
            borderRadius: 12,
            borderColor: error ? '#FB7672' : state.isFocused ? '#0B4598' : '#cfd6e6',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(11,69,152,0.15)' : 'none',
            paddingLeft: 8,
            paddingRight: 8,
            '&:hover': { borderColor: state.isFocused ? '#0B4598' : '#b8c2d9' },
        }),
        valueContainer: (base: CSSObjectWithLabel) => ({
            ...base,
            paddingTop: 8,
            paddingBottom: 8,
        }),
        placeholder: (base: CSSObjectWithLabel) => ({
            ...base,
            color: '#6b778c',
            fontSize: 16,
        }),
        input: (base: CSSObjectWithLabel) => ({
            ...base,
            fontSize: 16,
        }),
        multiValue: (base: CSSObjectWithLabel) => ({ ...base, display: 'none' }),
        multiValueLabel: (base: CSSObjectWithLabel) => ({ ...base, display: 'none' }),
        option: (base: CSSObjectWithLabel, state: { isFocused: boolean; isSelected: boolean }) => ({
            ...base,
            borderRadius: 8,
            backgroundColor: state.isFocused ? '#f0f6ff' : state.isSelected ? '#0B4598' : base.backgroundColor,
            color: state.isSelected ? '#ffffff' : base.color,
        }),
        menu: (base: CSSObjectWithLabel) => ({
            ...base,
            borderRadius: 12,
            overflow: 'hidden',
            width: '92%',
            left: '50%',
            transform: 'translateX(-50%)',
        }),
    }), [error]);

    // Build list of languages to display without duplicates (by id)
    const displayLanguages: Language[] = [];
    const seen = new Set<number>();
    [...commonLanguages, ...customOptions].forEach(lang => {
        if (!seen.has(lang.id)) {
            displayLanguages.push(lang);
            seen.add(lang.id);
        }
    });

    const handleAddOther = (opt: LangOption | null) => {
        if (!opt) return;
        const langObj = opt.lang;
        if (error) onClearError?.();
        if (!customOptions.some((l) => l.id === langObj.id)) {
            setCustomOptions((prev) => [...prev, langObj]);
        }
        if (!selectedIds.includes(langObj.id)) {
            setSelectedIds((prev) => [...prev, langObj.id]);
        }
        // leave input text as-is in uncontrolled mode
    };

    // Add no-fastclick directly to the underlying input to avoid long-press delay on Cordova
    const NoFastclickInput = (props: any) => {
        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Escape') {
                (e.target as HTMLElement).blur();
            }
            if (props.innerProps && typeof props.innerProps.onKeyDown === 'function') {
                props.innerProps.onKeyDown(e);
            }
        };
        const innerProps = {
            ...props.innerProps,
            className: ((props.innerProps?.className || '') + ' no-fastclick').trim(),
            onKeyDown: handleKeyDown,
        };
        return <components.Input {...props} innerProps={innerProps} />;
    };

    // Use default menu behavior (uncontrolled)

    return (
        <Container ref={containerRef} className="languagesSection">
            <div className="skills">Languages Spoken</div>
            {error && (
                <div className="error-text" role="alert">
                    {error}
                </div>
            )}
            <CommonLanguagesGrid>
                {displayLanguages.map((lang) => (
                    <FormControlLabel
                        key={lang.id}
                        className={'labelContainer languageLabelContainer inset no-fastclick'}
                        onMouseDown={(e) => {
                            e.preventDefault();
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleLanguage(lang, !selectedIds.includes(lang.id));
                        }}
                        control={
                            <Checkbox
                                name={`lang_${lang.id}`}
                                checked={selectedIds.includes(lang.id)}
                                icon={<img id="checkboxUnchecked" className="hidden-icon" alt="" src={'img/checkboxUnchecked.svg'} />}
                                checkedIcon={<img id="checkboxChecked" className="hidden-icon" alt="" src={'img/checkboxChecked.svg'} />}
                                disableRipple
                                tabIndex={-1}
                            />
                        }
                        label={<Label label={getDisplayName(lang)} description={''} style={error ? {color: '#FB7672'} : undefined} />}
                    />
                ))}
            </CommonLanguagesGrid>

            <div style={{ marginTop: '1rem', maxWidth: '32rem' }}>
                <Select
                    options={otherOptions}
                    value={null}
                    onChange={(opt: SingleValue<LangOption>) => handleAddOther(opt ?? null)}
                    filterOption={filterOption}
                    placeholder="Search and add other languages"
                    styles={selectStyles as any}
                    classNamePrefix="lang-select"
                    className="no-fastclick"
                    // uncontrolled input
                    isClearable
                    isMulti={false}
                    components={{ Input: NoFastclickInput }}
                    menuPlacement="auto"
                    menuShouldScrollIntoView={false}
                />
            </div>
        </Container>
    );
} 